import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import {
  fetchProducts,
  createProduct,
  updateProduct,
  hardDeleteProduct,
  setProductActive,
  addStockMovement,
} from "../../store/slices/inventorySlice";
import { customerService } from "../../services/customerService";
import { serviceService } from "../../services/serviceService";
import { staffService } from "../../services/staffService";
import { invoiceService } from "../../services/invoiceService";
import Icon from "../../components/Common/Icon";
import AdvancedTable from "../../components/Common/AdvancedTable";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

// Hareket türüne göre seçilebilir nedenler (yön ile tutarlı).
const REASONS_BY_TYPE = {
  IN: [
    { v: "PURCHASE", label: "Alım" },
    { v: "OTHER", label: "Diğer" },
  ],
  OUT: [
    { v: "SALE", label: "Satış" },
    { v: "SERVICE_USE", label: "Hizmette Tüketim" },
    { v: "WASTE", label: "Fire / Zaiat" },
    { v: "OTHER", label: "Diğer" },
  ],
  ADJUSTMENT: [{ v: "COUNT", label: "Sayım Düzeltmesi" }],
};

const KATEGORI_ONERILERI = [
  "Saç Boyası",
  "Şampuan & Bakım",
  "Cilt Bakımı",
  "Makyaj",
  "Tırnak",
  "Tek Kullanımlık",
  "Sarf Malzeme",
];

const BIRIMLER = ["ADET", "ML", "GR", "L", "KG", "PAKET", "KUTU"];

const fmtMoney = (v) =>
  v != null ? `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺` : "—";

const fmtQty = (v) =>
  v != null ? Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 2 }) : "0";

const emptyForm = {
  name: "",
  sku: "",
  category: "",
  unit: "ADET",
  costPrice: "",
  salePrice: "",
  commissionRate: "",
  quantity: "",
  minQuantity: "",
  description: "",
};

const emptyMovement = {
  type: "IN",
  reason: "PURCHASE",
  quantity: "",
  customerId: "",
  serviceId: "",
  staffId: "",
  movementDate: "",
  movementTime: "",
  note: "",
};

export default function InventoryPage() {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { items, isLoading, error } = useSelector((state) => state.inventory);
  const user = useSelector((state) => state.auth.user);
  const isManager = user?.role === "MANAGER";
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openCreate();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [onlyLowStock, setOnlyLowStock] = useState(false);

  // Stok hareketi modalı
  const [moveProduct, setMoveProduct] = useState(null);
  const [move, setMove] = useState(emptyMovement);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState(null);

  // Hareket modalı için hizmet/personel listeleri (satış/tüketim seçimleri).
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);

  // Müşteri seçimi arama tabanlı (binlerce kayıtta liste yüklemek yerine sunucuda arama).
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    dispatch(fetchProducts({ size: 200 }));
    serviceService
      .list({ size: 500, active: true })
      .then((res) => setServices(res.data.data?.content ?? []))
      .catch(() => {});
    staffService
      .list({ size: 500, active: true })
      .then((res) => setStaff(res.data.data?.content ?? []))
      .catch(() => {});
  }, [dispatch]);

  // Müşteri araması: yazarken 300ms debounce ile sunucuda arar (ilk 10 sonuç).
  useEffect(() => {
    const q = customerSearch.trim();
    if (q.length < 2) {
      setCustomerResults([]);
      return;
    }
    const t = setTimeout(() => {
      customerService
        .list({ size: 10, active: true, search: q })
        .then((res) => setCustomerResults(res.data.data?.content ?? []))
        .catch(() => setCustomerResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [customerSearch]);

  const refresh = () => dispatch(fetchProducts({ size: 200 }));

  const kategoriler = useMemo(() => {
    const set = new Set(KATEGORI_ONERILERI);
    items.forEach((p) => p.category && set.add(p.category));
    return Array.from(set);
  }, [items]);

  const visibleItems = useMemo(
    () => (onlyLowStock ? items.filter((p) => p.lowStock) : items),
    [items, onlyLowStock]
  );

  const lowStockCount = useMemo(() => items.filter((p) => p.lowStock).length, [items]);

  // --- Ürün formu ---
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name ?? "",
      sku: row.sku ?? "",
      category: row.category ?? "",
      unit: row.unit ?? "ADET",
      costPrice: row.costPrice != null ? String(row.costPrice) : "",
      salePrice: row.salePrice != null ? String(row.salePrice) : "",
      commissionRate: row.commissionRate != null ? String(row.commissionRate) : "",
      quantity: row.quantity != null ? String(row.quantity) : "",
      minQuantity: row.minQuantity != null ? String(row.minQuantity) : "",
      description: row.description ?? "",
    });
    setFormError(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    const base = {
      name: form.name.trim(),
      sku: form.sku.trim() || null,
      category: form.category.trim() || null,
      unit: form.unit || "ADET",
      costPrice: form.costPrice !== "" ? Number(form.costPrice) : null,
      salePrice: form.salePrice !== "" ? Number(form.salePrice) : null,
      commissionRate: form.commissionRate !== "" ? Number(form.commissionRate) : null,
      minQuantity: form.minQuantity !== "" ? Number(form.minQuantity) : 0,
      description: form.description.trim() || null,
    };
    try {
      if (editingId) {
        await dispatch(updateProduct({ id: editingId, payload: base })).unwrap();
        showToast("Ürün güncellendi.", "success");
      } else {
        await dispatch(
          createProduct({ ...base, quantity: form.quantity !== "" ? Number(form.quantity) : 0 })
        ).unwrap();
        showToast("Ürün eklendi.", "success");
      }
      closeModal();
      refresh();
    } catch (err) {
      setFormError(typeof err === "string" ? err : "İşlem başarısız.");
    } finally {
      setSaving(false);
    }
  };

  // --- Stok hareketi ---
  const [createInvoiceChecked, setCreateInvoiceChecked] = useState(false);
  const openMovement = (row) => {
    setMoveProduct(row);
    setMove(emptyMovement);
    setMoveError(null);
    setCreateInvoiceChecked(false);
    setSelectedCustomer(null);
    setCustomerSearch("");
    setCustomerResults([]);
  };
  const closeMovement = () => setMoveProduct(null);

  const handleMovement = async (e) => {
    e.preventDefault();
    setMoveError(null);
    if (move.quantity === "" || Number(move.quantity) <= 0) {
      setMoveError("Miktar 0'dan büyük olmalı.");
      return;
    }
    setMoving(true);
    const payload = {
      type: move.type,
      reason: move.reason || null,
      quantity: Number(move.quantity),
      customerId: move.reason === "SALE" && move.customerId ? move.customerId : null,
      serviceId: move.reason === "SERVICE_USE" && move.serviceId ? move.serviceId : null,
      // Satışı yapan personel prim tabanına girer; yalnız SALE'de gönderilir.
      staffId: move.reason === "SALE" && move.staffId ? move.staffId : null,
      movementDate: move.movementDate || null,
      movementTime: move.movementTime || null,
      note: move.note.trim() || null,
    };
    try {
      const movement = await dispatch(addStockMovement({ id: moveProduct.id, payload })).unwrap();
      if (move.reason === "SALE" && createInvoiceChecked) {
        const selectedCust = selectedCustomer;
        const invoicePayload = {
          customerId: move.customerId || null,
          documentType: "INVOICE",
          customerTitle: selectedCust ? selectedCust.fullName : "Perakende Müşteri",
          customerTaxOffice: "",
          customerTaxNo: "",
          customerAddress: selectedCust ? (selectedCust.address || "") : "",
          dueDate: new Date(Date.now() + 7 * 86400000).toISOString().split("T")[0],
          referenceType: "STOCK_MOVEMENT",
          referenceId: movement.id,
          items: [
            {
              itemName: moveProduct.name,
              itemType: "PRODUCT",
              quantity: Number(move.quantity),
              unit: moveProduct.unit || "ADET",
              unitPrice: Number(moveProduct.salePrice || 0) / 1.20,
              vatRate: 20,
              discountAmount: 0,
              referenceItemId: moveProduct.id
            }
          ]
        };
        await invoiceService.create(invoicePayload);
        showToast("Stok satışı ve faturası başarıyla kaydedildi.", "success");
      } else {
        showToast("Stok hareketi kaydedildi.", "success");
      }
      closeMovement();
      refresh();
    } catch (err) {
      setMoveError(typeof err === "string" ? err : "İşlem başarısız.");
    } finally {
      setMoving(false);
    }
  };

  const triggerHardDelete = (row) => {
    setConfirmModal({
      isOpen: true,
      title: "Ürünü Kalıcı Sil",
      message: `"${row.name}" ve tüm stok hareketleri kalıcı olarak silinecek. Emin misiniz?`,
      type: "danger",
      onConfirm: () =>
        dispatch(hardDeleteProduct(row.id))
          .unwrap()
          .then(() => showToast("Ürün silindi.", "success"))
          .catch((err) => showToast(err || "İşlem başarısız.", "error")),
    });
  };
  const toggleActive = (row) => {
    dispatch(setProductActive({ id: row.id, active: !row.active }))
      .unwrap()
      .then(() => showToast(row.active ? "Pasife alındı." : "Aktifleştirildi.", "success"))
      .catch((err) => showToast(err || "İşlem başarısız.", "error"));
  };

  const columns = [
    {
      key: "name",
      header: "Ürün",
      type: "string",
      sortable: true,
      render: (val, row) => (
        <div>
          <span className="font-semibold text-on-surface block">{val}</span>
          {row.sku && (
            <span className="text-[11px] text-on-surface-variant block">Kod: {row.sku}</span>
          )}
        </div>
      ),
    },
    { key: "category", header: "Kategori", type: "string", sortable: true, render: (v) => v || "—" },
    {
      key: "quantity",
      header: "Stok",
      type: "number",
      sortable: true,
      render: (val, row) => (
        <div className="flex items-center gap-2">
          <span className="font-semibold text-on-surface">
            {fmtQty(val)} <span className="text-[11px] font-normal text-on-surface-variant">{row.unit}</span>
          </span>
          {row.lowStock && (
            <span
              title={`Kritik eşik: ${fmtQty(row.minQuantity)}`}
              className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-error/15 text-error border border-error/20 flex items-center gap-0.5"
            >
              <Icon name="warning" className="!text-[12px]" /> Kritik
            </span>
          )}
        </div>
      ),
    },
    { key: "salePrice", header: "Satış", type: "number", sortable: true, render: (v) => fmtMoney(v) },
    { key: "costPrice", header: "Alış", type: "number", sortable: true, render: (v) => fmtMoney(v) },
    {
      key: "active",
      header: "Durum",
      sortable: true,
      render: (val) => (
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            val
              ? "bg-success/15 text-success border border-success/20"
              : "bg-outline-variant/30 text-on-surface-variant border border-outline-variant/40"
          }`}
        >
          {val ? "Aktif" : "Pasif"}
        </span>
      ),
    },
  ];

  // Stok hareketi (giriş/çıkış) ve yeni ürün ekleme hem MANAGER hem STAFF'a açık;
  // ürün düzenle/pasife al/kalıcı sil yalnız MANAGER'a açıktır.
  const renderActions = (p) => (
    <div className="flex items-center justify-end gap-1.5">
      <button
        onClick={() => openMovement(p)}
        title="Stok Giriş/Çıkış"
        className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
      >
        <Icon name="swap_vert" className="!text-[18px]" />
      </button>
      {isManager && (
        <>
          <button
            onClick={() => openEdit(p)}
            title="Düzenle"
            className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
          >
            <Icon name="edit" className="!text-[18px]" />
          </button>
          <button
            onClick={() => toggleActive(p)}
            title={p.active ? "Pasife Al" : "Aktifleştir"}
            className={`p-1.5 rounded-lg transition ${
              p.active ? "text-tier-gold hover:bg-tier-gold/10" : "text-success hover:bg-success/10"
            }`}
          >
            <Icon name={p.active ? "pause_circle" : "play_circle"} className="!text-[18px]" />
          </button>
          <button
            onClick={() => triggerHardDelete(p)}
            title="Kalıcı Sil"
            className="p-1.5 rounded-lg text-error hover:bg-error/10 transition"
          >
            <Icon name="delete_forever" className="!text-[18px]" />
          </button>
        </>
      )}
    </div>
  );

  const inputCls =
    "w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary";
  const labelCls = "block text-xs font-semibold mb-1 text-on-surface-variant";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="inventory_2" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Stok Yönetimi</h1>
            <p className="text-on-surface-variant text-sm">
              Ürün ve malzeme kartları, stok giriş/çıkış hareketleri.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isManager && (
            <Link
              to="/inventory/reports"
              className="rounded-xl px-3 py-2 font-semibold text-sm border transition flex items-center gap-1.5 bg-white text-on-surface-variant border-outline-variant hover:bg-surface-container-high"
            >
              <Icon name="analytics" className="!text-[18px]" />
              Stok Analizi
            </Link>
          )}
          <button
            onClick={() => setOnlyLowStock((v) => !v)}
            className={`rounded-xl px-3 py-2 font-semibold text-sm border transition flex items-center gap-1.5 ${
              onlyLowStock
                ? "bg-error/10 text-error border-error/30"
                : "bg-white text-on-surface-variant border-outline-variant hover:bg-surface-container-high"
            }`}
          >
            <Icon name="warning" className="!text-[18px]" />
            Kritik Stok
            {lowStockCount > 0 && (
              <span className="bg-error text-white rounded-full text-[10px] px-1.5 py-0.5 leading-none">
                {lowStockCount}
              </span>
            )}
          </button>
          {/* Yeni ürün ekleme hem MANAGER hem STAFF'a açık (stok işlemleri kapsamında). */}
          <button
            onClick={openCreate}
            className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-sm hover:opacity-90 transition flex items-center gap-1.5"
          >
            <Icon name="add" className="!text-[18px]" /> Yeni Ürün
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
          Yükleniyor…
        </div>
      ) : (
        <AdvancedTable
          columns={columns}
          data={visibleItems}
          actions={renderActions}
          searchPlaceholder="Ürün adı veya kodu ara..."
        />
      )}

      {/* Ürün ekle/düzenle modalı */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="inventory_2" className="text-primary !text-[24px]" />
                <h3 className="font-bold text-lg text-on-surface">
                  {editingId ? "Ürünü Düzenle" : "Yeni Ürün"}
                </h3>
              </div>
              <button onClick={closeModal} className="p-1 rounded-full text-on-surface-variant hover:text-primary transition">
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4 flex-1 text-sm">
              {formError && (
                <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5">
                  <Icon name="error" className="!text-[16px]" /> {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Ürün Adı *</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Stok Kodu / Barkod</label>
                  <input
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Kategori</label>
                  <input
                    list="urun-kategori-list"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="Örn. Saç Boyası"
                    className={inputCls}
                  />
                  <datalist id="urun-kategori-list">
                    {kategoriler.map((k) => (
                      <option key={k} value={k} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelCls}>Birim</label>
                  <select
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    className={inputCls}
                  >
                    {BIRIMLER.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Kritik Stok Eşiği</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.minQuantity}
                    onChange={(e) => setForm({ ...form, minQuantity: e.target.value })}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Alış Fiyatı (₺)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.costPrice}
                    onChange={(e) => setForm({ ...form, costPrice: e.target.value })}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Satış Fiyatı (₺)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.salePrice}
                    onChange={(e) => setForm({ ...form, salePrice: e.target.value })}
                    className={inputCls}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className={labelCls}>Satış Primi Oranı (%)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.commissionRate}
                    onChange={(e) => setForm({ ...form, commissionRate: e.target.value })}
                    className={inputCls}
                  />
                  <p className="text-[11px] text-on-surface-variant mt-1">
                    Bu ürünü satan personelin primi: satış tutarı × bu oran. Boş bırakılırsa bu üründen prim hesaplanmaz.
                  </p>
                </div>

                {!editingId && (
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Başlangıç Stoğu</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      className={inputCls}
                    />
                    <p className="text-[11px] text-on-surface-variant mt-1">
                      Sonraki stok değişiklikleri giriş/çıkış hareketleriyle yapılır.
                    </p>
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label className={labelCls}>Açıklama</label>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs">
                  İptal
                </button>
                <button type="submit" disabled={saving} className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5">
                  {saving ? "Kaydediliyor…" : (<><Icon name="save" className="!text-[16px]" /> Kaydet</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stok hareketi modalı */}
      {moveProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-md w-full flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="swap_vert" className="text-primary !text-[24px]" />
                <h3 className="font-bold text-lg text-on-surface">Stok Hareketi</h3>
              </div>
              <button onClick={closeMovement} className="p-1 rounded-full text-on-surface-variant hover:text-primary transition">
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>

            <form onSubmit={handleMovement} className="p-6 space-y-4 text-sm">
              <div className="bg-surface-container-low rounded-xl px-4 py-3 flex items-center justify-between">
                <span className="font-semibold text-on-surface">{moveProduct.name}</span>
                <span className="text-on-surface-variant">
                  Mevcut: <b className="text-on-surface">{fmtQty(moveProduct.quantity)}</b> {moveProduct.unit}
                </span>
              </div>

              {moveError && (
                <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5">
                  <Icon name="error" className="!text-[16px]" /> {moveError}
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: "IN", label: "Giriş", icon: "add", cls: "text-success border-success/40 bg-success/10" },
                  { v: "OUT", label: "Çıkış", icon: "remove", cls: "text-error border-error/40 bg-error/10" },
                  { v: "ADJUSTMENT", label: "Düzeltme", icon: "tune", cls: "text-primary border-primary/40 bg-primary/10" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() =>
                      setMove({
                        ...move,
                        type: opt.v,
                        reason: REASONS_BY_TYPE[opt.v][0].v,
                        customerId: "",
                        serviceId: "",
                      })
                    }
                    className={`rounded-xl py-2 font-semibold text-xs border transition flex flex-col items-center gap-0.5 ${
                      move.type === opt.v
                        ? opt.cls
                        : "text-on-surface-variant border-outline-variant hover:bg-surface-container-high"
                    }`}
                  >
                    <Icon name={opt.icon} className="!text-[18px]" />
                    {opt.label}
                  </button>
                ))}
              </div>

              <div>
                <label className={labelCls}>
                  {move.type === "ADJUSTMENT" ? "Yeni Stok Değeri *" : "Miktar *"}
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={move.quantity}
                  onChange={(e) => setMove({ ...move, quantity: e.target.value })}
                  className={inputCls}
                />
                {move.type === "ADJUSTMENT" && (
                  <p className="text-[11px] text-on-surface-variant mt-1">
                    Sayım sonrası stok bu değere ayarlanır.
                  </p>
                )}
              </div>

              <div>
                <label className={labelCls}>Neden</label>
                <select
                  value={move.reason}
                  onChange={(e) =>
                    setMove({ ...move, reason: e.target.value, customerId: "", serviceId: "", staffId: "" })
                  }
                  className={inputCls}
                >
                  {(REASONS_BY_TYPE[move.type] ?? []).map((r) => (
                    <option key={r.v} value={r.v}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              {move.reason === "SALE" && (
                <div>
                  <label className={labelCls}>Müşteri (kime satıldı)</label>
                  {selectedCustomer ? (
                    <div className={`${inputCls} flex items-center justify-between !py-2`}>
                      <span className="truncate">{selectedCustomer.fullName}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCustomer(null);
                          setMove({ ...move, customerId: "" });
                          setCustomerSearch("");
                          setCustomerResults([]);
                        }}
                        className="text-on-surface-variant hover:text-error ml-2 shrink-0"
                      >
                        <Icon name="close" className="!text-[18px]" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        placeholder="Müşteri adıyla ara…"
                        className={inputCls}
                      />
                      {customerResults.length > 0 && (
                        <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-xl border border-outline-variant bg-surface-container shadow-lg">
                          {customerResults.map((c) => (
                            <li key={c.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedCustomer(c);
                                  setMove({ ...move, customerId: c.id });
                                  setCustomerSearch("");
                                  setCustomerResults([]);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-surface-container-high"
                              >
                                {c.fullName}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}

              {move.reason === "SALE" && (
                <div>
                  <label className={labelCls}>Satışı Yapan Personel</label>
                  <select
                    value={move.staffId}
                    onChange={(e) => setMove({ ...move, staffId: e.target.value })}
                    className={inputCls}
                  >
                    <option value="">— Seçilmedi —</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.fullName}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-on-surface-variant mt-1">
                    Seçilmezse bu satıştan kimseye prim yazılmaz.
                  </p>
                </div>
              )}

              {move.reason === "SERVICE_USE" && (
                <div>
                  <label className={labelCls}>Hizmet (nerede tüketildi)</label>
                  <select
                    value={move.serviceId}
                    onChange={(e) => setMove({ ...move, serviceId: e.target.value })}
                    className={inputCls}
                  >
                    <option value="">— Seçilmedi —</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Tarih</label>
                  <input
                    type="date"
                    value={move.movementDate}
                    onChange={(e) => setMove({ ...move, movementDate: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Saat</label>
                  <input
                    type="time"
                    value={move.movementTime}
                    onChange={(e) => setMove({ ...move, movementTime: e.target.value })}
                    className={inputCls}
                  />
                  <p className="text-[11px] text-on-surface-variant mt-1">Boşsa kayıt saati alınır.</p>
                </div>
              </div>

              <div>
                <label className={labelCls}>Not</label>
                <input
                  value={move.note}
                  onChange={(e) => setMove({ ...move, note: e.target.value })}
                  placeholder="Örn. Tedarikçi alımı, fire, sayım"
                  className={inputCls}
                />
              </div>

              {move.reason === "SALE" && (
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="createInvoiceChecked"
                    checked={createInvoiceChecked}
                    onChange={(e) => setCreateInvoiceChecked(e.target.checked)}
                    className="w-4 h-4 text-primary border-outline-variant rounded focus:ring-primary"
                  />
                  <label htmlFor="createInvoiceChecked" className="text-xs font-semibold text-on-surface">
                    Bu Satış için Resmi Fatura Kes / Al
                  </label>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={closeMovement} className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs">
                  İptal
                </button>
                <button type="submit" disabled={moving} className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5">
                  {moving ? "Kaydediliyor…" : (<><Icon name="save" className="!text-[16px]" /> Kaydet</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
